import {
    opportunityHasSyncedTitleContact,
    syncedTitleContactName,
} from '../../helpers/titleContactStatus';
import { extractTitleSearchOwners } from '../../helpers/titleSearchOwners';
import opportunityInfoService from '../../services/oneButtonServices/opportunityInfoService';
import { useOneButtonStore } from '../../store/oneButtonStore';

export type PreContractTitleFields = {
    owner: string;
    apn: string;
    fips: string;
    propertyType: string;
};

export type PreContractTitleContext = {
    titleInfo: Record<string, unknown> | null;
    titleSearchData: unknown[];
    titleContactSynced: boolean;
    titleContactName: string;
};

function pickStr(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
    if (!obj) return '';
    for (const key of keys) {
        const value = obj[key];
        if (value == null) continue;
        const text = String(value).trim();
        if (text !== '') return text;
    }
    return '';
}

function asTitleRecord(raw: unknown): Record<string, unknown> | null {
    if (raw == null) return null;
    if (Array.isArray(raw)) {
        const first = raw[0];
        return first && typeof first === 'object' ? (first as Record<string, unknown>) : null;
    }
    if (typeof raw === 'object') return raw as Record<string, unknown>;
    return null;
}

export function extractTitleFields(raw: unknown): PreContractTitleFields {
    const record = asTitleRecord(raw) ?? {};
    const useCode = pickStr(record, ['useCode', 'UseCode']);
    const useCodeDesc = pickStr(record, [
        'useCodeDescription',
        'UseCodeDescription',
        'unitCode',
        'UnitCode',
    ]);
    let propertyType = '';
    if (useCode && useCodeDesc) propertyType = `${useCode} — ${useCodeDesc}`;
    else propertyType = useCode || useCodeDesc;

    return {
        owner: pickStr(record, ['owner', 'Owner', 'OwnerName', 'ownerName']),
        apn: pickStr(record, ['apn', 'APN', 'parcelId', 'ParcelId', 'legalDescription']),
        fips: pickStr(record, ['fips', 'FIPS', 'fipsCode']),
        propertyType,
    };
}

export function isTitleInfoAvailable(raw: unknown): boolean {
    const fields = extractTitleFields(raw);
    return Boolean(fields.owner || fields.apn || fields.propertyType || fields.fips);
}

export function isTitleStepComplete(
    titleInfo: unknown,
    opportunity: unknown,
): boolean {
    return isTitleInfoAvailable(titleInfo) || opportunityHasSyncedTitleContact(opportunity);
}

export function hasTitleSearchOwners(titleSearchData: unknown[] | null | undefined): boolean {
    return Array.isArray(titleSearchData) && titleSearchData.length > 0;
}

function isValidOpportunityId(value: unknown): value is string | number {
    if (value == null || value === '') return false;
    const text = String(value).trim();
    return text !== '' && text !== 'undefined' && text !== 'null';
}

/** Numeric Trinity opportunity id from an API opportunity payload. */
export function opportunityNumericId(opportunity: unknown): string | null {
    if (!opportunity || typeof opportunity !== 'object') return null;
    const record = opportunity as Record<string, unknown>;
    for (const key of ['Opportunity_Id', 'OpportunityId', 'opportunity_Id', 'opportunityId']) {
        const value = record[key];
        if (isValidOpportunityId(value)) return String(value).trim();
    }
    return null;
}

/** Resolve opportunity id already present in the oneBUTTON store. */
export function resolveStoreOpportunityId(): string | null {
    const store = useOneButtonStore.getState();
    const candidates = [
        store.opportunityDetails?.Opportunity_Id,
        store.opportunityDetails?.OpportunityId,
        store.opportunityID,
        store.systemDesign?.Opportunity_Id,
        store.systemDesign?.OpportunityId,
    ];
    for (const candidate of candidates) {
        if (isValidOpportunityId(candidate)) return String(candidate).trim();
    }
    return null;
}

/** Ensure opportunity detail + numeric id are in the store (fetch if missing). */
export async function hydrateOpportunityStore(salesForceId: string): Promise<string | null> {
    const sfid = salesForceId.trim();
    if (!sfid) return null;

    const existing = resolveStoreOpportunityId();
    if (existing) return existing;

    try {
        const detailRaw = await opportunityInfoService.getOpportunityDetail(sfid);
        const opportunity = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
        if (!opportunity) return null;

        const store = useOneButtonStore.getState();
        store.setSalesForceId(sfid);
        const oppId = opportunityNumericId(opportunity);
        store.setOpportunityID(oppId);
        store.setOpportunityDetails(opportunity);
        return oppId;
    } catch {
        return null;
    }
}

export async function loadPreContractTitleContext(
    salesForceId: string,
): Promise<PreContractTitleContext> {
    const sfid = salesForceId.trim();
    const empty: PreContractTitleContext = {
        titleInfo: null,
        titleSearchData: [],
        titleContactSynced: false,
        titleContactName: '',
    };
    if (!sfid) return empty;

    try {
        const detailRaw = await opportunityInfoService.getOpportunityDetail(sfid);
        const opportunity = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
        const store = useOneButtonStore.getState();
        store.setSalesForceId(sfid);
        if (opportunity) {
            store.setOpportunityID(opportunityNumericId(opportunity));
            store.setOpportunityDetails(opportunity);
        }

        const titleContactSynced = opportunityHasSyncedTitleContact(opportunity);
        const titleContactName = syncedTitleContactName(opportunity);

        const directLeadId = opportunity?.DirectLeadID ?? opportunity?.DirectLeadId ?? opportunity?.directLeadID;
        const addr = opportunity?.InstallationAddress;
        if (!directLeadId || !addr) {
            store.setTitleInfo(null);
            return { ...empty, titleContactSynced, titleContactName };
        }

        const [titleRes, searchRes] = await Promise.all([
            opportunityInfoService.getCustomerTitleInfo(directLeadId, addr).catch(() => null),
            opportunityInfoService
                .getCustomerTitleInfooneLead(
                    String(directLeadId),
                    addr?.Street || '',
                    String(addr?.ZipCode || addr?.zipCode || '').slice(0, 5),
                    String(addr?.Latitude ?? ''),
                    String(addr?.Longitude ?? ''),
                )
                .catch(() => null),
        ]);

        const titleInfo =
            titleRes?.data?.locations?.[0] ?? titleRes?.locations?.[0] ?? null;
        store.setTitleInfo(titleInfo ?? null);

        return {
            titleInfo: titleInfo && typeof titleInfo === 'object' ? titleInfo : null,
            titleSearchData,
            titleContactSynced,
            titleContactName,
        };
    } catch {
        return empty;
    }
}
